import { InstancedMesh, type BufferGeometry, type Material, type Mesh, type Object3D } from "three";

/** Um lote: todas as malhas do quadro com a mesma geometria e o mesmo material, num draw call. */
interface Batch {
  mesh: InstancedMesh;
  geometry: BufferGeometry;
  material: Material;
  renderOrder: number;
  parts: Mesh[];
}

/**
 * Desenho em lotes (F36): as malhas de quem usa (bonecos de criaturas, estruturas) ficam fora da
 * cena, só para as transformações; a cada quadro, cada malha visível entra como instância no lote da
 * sua geometria e material. O custo em draw calls passa a ser o número de combinações diferentes, não
 * o de malhas. Os materiais precisam aplicar `instanceMatrix` (o toon aplica).
 */
export class InstanceBatcher {
  private readonly root: Object3D;
  private readonly name: string;
  private readonly batches = new Map<string, Batch>();

  constructor(root: Object3D, name: string) {
    this.root = root;
    this.name = name;
  }

  /** Começa um quadro (esvazia os lotes). */
  begin(): void {
    for (const batch of this.batches.values()) batch.parts.length = 0;
  }

  /**
   * As malhas visíveis sob `object` (com o próprio `object`), com as matrizes do mundo atualizadas
   * agora (o objeto fica fora da cena).
   */
  addTree(object: Object3D): void {
    if (!object.visible) return;
    object.updateMatrixWorld(true);
    this.collect(object);
  }

  private collect(o: Object3D): void {
    if (!o.visible) return;
    if ((o as Mesh).isMesh) this.add(o as Mesh);
    for (const child of o.children) this.collect(child);
  }

  /** Uma malha (com `matrixWorld` já atualizada). */
  add(part: Mesh): void {
    const material = part.material as Material;
    const key = `${part.geometry.uuid}|${material.uuid}`;
    let batch = this.batches.get(key);
    if (!batch) {
      batch = { mesh: this.newMesh(part.geometry, material, 8, part.renderOrder), geometry: part.geometry, material, renderOrder: part.renderOrder, parts: [] };
      this.root.add(batch.mesh);
      this.batches.set(key, batch);
    }
    batch.parts.push(part);
  }

  /** Fecha o quadro: matrizes das instâncias, contagens e limites para o corte de visão. */
  end(): void {
    for (const batch of this.batches.values()) {
      const n = batch.parts.length;
      if (n > batch.mesh.instanceMatrix.count) {
        // Cresce (dobrando): troca a malha do lote por uma com mais vagas.
        this.root.remove(batch.mesh);
        batch.mesh.dispose();
        batch.mesh = this.newMesh(batch.geometry, batch.material, Math.max(n, batch.mesh.instanceMatrix.count * 2), batch.renderOrder);
        this.root.add(batch.mesh);
      }
      for (let i = 0; i < n; i++) batch.mesh.setMatrixAt(i, (batch.parts[i] as Mesh).matrixWorld);
      batch.mesh.count = n;
      batch.mesh.visible = n > 0;
      if (n > 0) {
        batch.mesh.instanceMatrix.needsUpdate = true;
        batch.mesh.computeBoundingSphere();
      }
    }
  }

  /** Lotes com alguma instância no último quadro (= draw calls). */
  get count(): number {
    let n = 0;
    for (const b of this.batches.values()) if (b.parts.length > 0) n++;
    return n;
  }

  dispose(): void {
    for (const batch of this.batches.values()) {
      this.root.remove(batch.mesh);
      batch.mesh.dispose();
    }
    this.batches.clear();
  }

  private newMesh(geometry: BufferGeometry, material: Material, capacity: number, renderOrder: number): InstancedMesh {
    const mesh = new InstancedMesh(geometry, material, capacity);
    mesh.count = 0;
    mesh.renderOrder = renderOrder;
    mesh.name = this.name;
    return mesh;
  }
}
